// Visualizador de JSON interativo (árvore colapsável) no estilo
// react-json-view (uiwjs) — mesma família do CodeMirror já usado no
// editor. Cada nó colapsa/expande, objetos/arrays exibem contagem
// ({n items}), e há cópia por nó. Mantemos por cima o header de
// "painel de código" (nome do arquivo + tamanho + copiar tudo com
// feedback "Copiado ✓" por 2s). Tema LIGHT (padrão do portal): painel
// claro, cores tipo github-light (chave sky, string green, número
// âmbar). Reutilizável (detalhe + aba "Dados de exemplo" do editor).
import JsonView from "@uiw/react-json-view"
import { CheckIcon, CopyIcon } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

import { formatNotificationBytes } from "../notification-detail-utils"

const viewerPrefix = "common.jsonViewer"
const COPY_FEEDBACK_MS = 2_000

// Colapsa a partir do 2º nível: os ramos de topo (customer,
// subscription, payment…) já nascem visíveis, o resto expande sob
// demanda — evita o "paredão" que o payload cru gerava.
const COLLAPSE_LEVEL = 2

// Tema LIGHT (github-light-like) via CSS custom properties do
// react-json-view: fundo claro, chave sky-700, string green-700,
// número âmbar-700, boolean/null violeta, estrutura em slate. Casa
// com o portal claro (≠ da ilha escura anterior).
const LIGHT_JSON_THEME: React.CSSProperties = {
  "--w-rjv-font-family":
    "ui-monospace, SFMono-Regular, Menlo, monospace",
  "--w-rjv-background-color": "transparent", // usa o bg do card
  "--w-rjv-color": "#334155", // slate-700 (base)
  "--w-rjv-line-color": "#e2e8f0", // slate-200 (guias de indentação)
  "--w-rjv-arrow-color": "#94a3b8", // slate-400 (setas colapsar)
  "--w-rjv-info-color": "#94a3b8", // slate-400 ({n items})
  "--w-rjv-ellipsis-color": "#94a3b8",
  "--w-rjv-key-string": "#0369a1", // sky-700 (chaves)
  "--w-rjv-key-number": "#0369a1",
  "--w-rjv-colon-color": "#64748b", // slate-500
  "--w-rjv-quotes-color": "#0369a1", // aspas das chaves
  "--w-rjv-quotes-string-color": "#15803d", // aspas das strings
  "--w-rjv-brackets-color": "#64748b", // [ ]
  "--w-rjv-curlybraces-color": "#64748b", // { }
  "--w-rjv-type-string-color": "#15803d", // green-700
  "--w-rjv-type-int-color": "#b45309", // amber-700
  "--w-rjv-type-float-color": "#b45309",
  "--w-rjv-type-bigint-color": "#b45309",
  "--w-rjv-type-boolean-color": "#7c3aed", // violet-600
  "--w-rjv-type-null-color": "#7c3aed",
  "--w-rjv-type-nan-color": "#7c3aed",
  "--w-rjv-type-undefined-color": "#7c3aed",
  "--w-rjv-type-date-color": "#64748b",
  "--w-rjv-type-url-color": "#0284c7", // sky-600 (links)
  "--w-rjv-copied-color": "#94a3b8",
  "--w-rjv-copied-success-color": "#16a34a", // green-600
} as React.CSSProperties

export const JsonViewer = ({
  value,
  sizeBytes,
  fileName = "payload.json",
}: {
  value: unknown
  // Tamanho persistido (dataInformation.sizeBytes); quando ausente,
  // calculado do próprio stringify.
  sizeBytes?: number
  fileName?: string
}) => {
  const { t, i18n } = useTranslation()
  const [isCopied, setIsCopied] = useState(false)
  const copyTimerRef = useRef<number>()

  const compactJson = useMemo(
    () => JSON.stringify(value),
    [value]
  )
  const effectiveSizeBytes =
    sizeBytes ??
    new TextEncoder().encode(compactJson).length

  // Evita setState após unmount (feedback de 2s do copiar).
  useEffect(
    () => () => window.clearTimeout(copyTimerRef.current),
    []
  )

  const handleCopy = () => {
    // Copia o JSON FORMATADO (indentado) — a árvore é visual, mas
    // quem copia geralmente cola num editor.
    navigator.clipboard?.writeText(
      JSON.stringify(value, null, 2)
    )
    setIsCopied(true)
    window.clearTimeout(copyTimerRef.current)
    copyTimerRef.current = window.setTimeout(
      () => setIsCopied(false),
      COPY_FEEDBACK_MS
    )
  }

  return (
    <div
      data-testid="json-viewer"
      className="overflow-hidden rounded-lg border border-border bg-card shadow-sm"
    >
      {/* Header do painel (estilo aba de editor). */}
      <div className="flex items-center justify-between border-b border-border bg-muted/50 px-3 py-1.5">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-muted-foreground">
            {fileName}
          </span>
          <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
            {formatNotificationBytes(
              effectiveSizeBytes,
              i18n.language
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          aria-label={t(`${viewerPrefix}.ariaLabel`)}
          className={cn(
            "flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            isCopied
              ? "text-emerald-600"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          )}
        >
          {isCopied ? (
            <>
              <CheckIcon size={12} aria-hidden="true" />
              {t(`${viewerPrefix}.copied`)}
            </>
          ) : (
            <>
              <CopyIcon size={12} aria-hidden="true" />
              {t(`${viewerPrefix}.copy`)}
            </>
          )}
        </button>
      </div>
      {/* Corpo: árvore JSON interativa (colapsável). */}
      <div className="max-h-96 overflow-auto px-3 py-2.5 text-xs leading-relaxed">
        <JsonView
          value={value as object}
          style={LIGHT_JSON_THEME}
          collapsed={COLLAPSE_LEVEL}
          // Sem prefixo de tipo ("int"/"string") — leitura limpa,
          // como o painel de logs. Contagem de itens fica.
          displayDataTypes={false}
          displayObjectSize
          // Não truncar strings longas (URLs de recuperação etc.).
          shortenTextAfterLength={0}
          indentWidth={16}
          enableClipboard
        />
      </div>
    </div>
  )
}
